import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import { Container } from "@pixi/react";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import WinTextController from "./WinTextController";
import { safeInit } from "../../../utilities";
import {
  selectWinAnimationsPlaying,
  selectIsAnimatedWin,
} from "../../../State/selectors";
import { Settings } from "../../../settings";

const WinText = forwardRef(
  (
    { fontSizeMultiplier = 1.0, yPosition = null, hideOnBigWin = false },
    ref,
  ) => {
    const containerRef = useRef(null);
    const controllerRef = useRef(null);
    const { symbolWidth, symbolHeight } = useDimensions();
    const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
    const isAnimatedWin = useSelector(selectIsAnimatedWin);

    // Default Y: between the last symbol and the one above it
    // Reel center is y=0. Symbols span from -SYMBOLS_VISIBLE/2 to +SYMBOLS_VISIBLE/2 in symbolHeight units.
    // The boundary between last (index N-1) and second-to-last (index N-2) is at:
    // (SYMBOLS_VISIBLE/2 - 1) * symbolHeight
    const containerY = useMemo(
      () =>
        yPosition !== null
          ? yPosition
          : Math.round(symbolHeight * (Settings.SYMBOLS_VISIBLE / 2 - 1)),
      [symbolHeight, yPosition],
    );

    // Initialize controller when container is ready (after render completes)
    useEffect(() => {
      if (containerRef.current && !controllerRef.current) {
        safeInit(() => {
          controllerRef.current = new WinTextController(
            containerRef.current,
            symbolWidth,
            symbolHeight,
            fontSizeMultiplier,
          );
        });
      }

      return () => {
        if (controllerRef.current) {
          controllerRef.current.cleanup();
          controllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle dimension changes
    useEffect(() => {
      if (controllerRef.current && symbolWidth && symbolHeight) {
        controllerRef.current.updateDimensions(symbolWidth, symbolHeight);
      }
    }, [symbolWidth, symbolHeight]);

    useImperativeHandle(ref, () => ({
      showWinAmount: (amount) => {
        controllerRef.current?.showWinAmount(amount);
      },
      hide: () => {
        controllerRef.current?.hide();
      },
      getSpineInstance: () => {
        return controllerRef.current?.getSpineInstance();
      },
    }));

    // Determine visibility based on hideOnBigWin prop
    const isVisible = useMemo(() => {
      if (hideOnBigWin) {
        // Hide during big win animations (for global win text)
        return !isAnimatedWin || !winAnimationsPlaying;
      }
      // Always visible (for line win text)
      return true;
    }, [hideOnBigWin, isAnimatedWin, winAnimationsPlaying]);

    return (
      <Container
        ref={containerRef}
        y={containerY}
        x={0}
        zIndex={12}
        sortableChildren
        name={`win_text_${fontSizeMultiplier}`}
        visible={isVisible}
      />
    );
  },
);

export default WinText;
